import { z } from 'zod';

import { GlobalResponseSchema } from './global-schema';

/** Delay before offer collection runs (minutes); null = server default. */
export const offerCollectionDelayMinutesSchema = z
  .number()
  .int()
  .min(1)
  .max(10080)
  .nullable()
  .optional();

/** IANA time zone (e.g. America/New_York); null/omit = server default. */
export const brokerTimeZoneSchema = z.string().max(128).nullable().optional();

/**
 * GET …/broker/profile/ may expose the zone as `time_zone` or `timezone`.
 * Normalize so the app always reads `time_zone` from `data`.
 */
function brokerProfileDataFromApi(raw: unknown): unknown {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return raw;
  const o = raw as Record<string, unknown>;
  const tz = o.time_zone ?? o.timezone;
  return { ...o, time_zone: tz };
}

const brokerProfileDataFieldsSchema = z.object({
  firstname: z.string().min(1),
  lastname: z.string().min(1),
  title: z.string().min(1),
  offer_collection_delay_minutes: offerCollectionDelayMinutesSchema,
  time_zone: brokerTimeZoneSchema,
});

/** Parsed `data` from GET …/broker/profile/ */
export const BrokerProfileDataSchema = z.preprocess(
  brokerProfileDataFromApi,
  brokerProfileDataFieldsSchema,
);

export const BrokerProfileResponseSchema = GlobalResponseSchema.extend({
  data: BrokerProfileDataSchema,
});

/** PUT …/broker/profile/ */
export const BrokerProfileUpdateBodySchema = z.object({
  firstname: z.string().max(255).min(1),
  lastname: z.string().max(255).min(1),
  title: z.string().max(100).min(1),
  offer_collection_delay_minutes: offerCollectionDelayMinutesSchema,
  timezone: brokerTimeZoneSchema,
});

export const BrokerProfileUpdateResponseSchema =
  GlobalResponseSchema.passthrough();
